import type { Bot, BotType, ConversationPersistence } from '@/services/types'
import { isEmbeddedWindow } from '@/utils/windowContext'

export type ChatSurface =
  | 'desktop_workspace'
  | 'helper_embed'
  | 'canvas_workspace'
  | 'canvas_bot'
  | 'canvas_assignment'
export type LaunchContext = 'cas' | 'lti_study' | 'lti_assignment'
export type SessionScope = 'user_all' | 'bot_only' | 'fixed_session'
export type BotCatalogScope = 'all_allowed' | 'academic_only' | 'single_bot'
export type UiPolicyPreset =
  | 'desktop_standard'
  | 'embedded_helper'
  | 'canvas_workspace'
  | 'canvas_student'
  | 'assignment_locked'

export interface SurfaceExperienceConfig {
  conversationMode?: ConversationPersistence
  sessionScope?: SessionScope
  botCatalogScope?: BotCatalogScope
  uiPolicy?: UiPolicyPreset
}

export interface AppBotConfig {
  id: string
  name?: string
  type?: BotType
  isStatic?: boolean
  requiresAuth?: boolean
  allowAnonymousHistory?: boolean
  surfaces?: Partial<Record<ChatSurface, SurfaceExperienceConfig>>
}

export interface AppConfig {
  apiBaseUrl: string
  bearerToken?: string
  casBaseUrl?: string
  casLoginUrl?: string
  casLogoutUrl?: string
  defaultAnonymousConversationMode?: ConversationPersistence
  staticBotId?: string
  bots?: AppBotConfig[]
  surfaces?: Partial<Record<ChatSurface, SurfaceExperienceConfig>>
}

export interface ConfiguredBot extends Bot {
  isStatic: boolean
  requiresAuth: boolean
  allowAnonymousHistory: boolean
  surfaces: Partial<Record<ChatSurface, SurfaceExperienceConfig>>
}

export interface RuntimeContext {
  surface: ChatSurface
  launchContext: LaunchContext
  isEmbedded: boolean
}

let configCache: AppConfig | null = null
const DEFAULT_CONFIG_FILE = 'config.json'

function getConfigFileName(): string {
  const configured = import.meta.env.VITE_APP_CONFIG_NAME
  return typeof configured === 'string' && configured.trim()
    ? configured.trim()
    : DEFAULT_CONFIG_FILE
}

export async function loadConfig(): Promise<AppConfig> {
  if (configCache) return configCache
  
  const response = await fetch(`/${getConfigFileName()}`)
  configCache = await response.json()
  return configCache!
}

// Sync access after config is loaded
export function getConfig(): AppConfig {
  if (!configCache) throw new Error('Config not loaded. Call loadConfig() first.')
  return configCache
}

function toConfiguredBot(config: AppBotConfig): ConfiguredBot {
  return {
    id: config.id,
    name: config.name ?? config.id,
    type: config.type ?? 'departmental',
    isStatic: config.isStatic ?? false,
    requiresAuth: config.requiresAuth ?? false,
    allowAnonymousHistory: config.allowAnonymousHistory ?? false,
    surfaces: config.surfaces ?? {},
  }
}

function isEmbeddedContext(): boolean {
  return isEmbeddedWindow()
}

export function getConfiguredBots(): ConfiguredBot[] {
  const config = getConfig()
  const configuredBots = (config.bots ?? []).map(toConfiguredBot)

  if (config.staticBotId) {
    return configuredBots.map(bot =>
      bot.id === config.staticBotId ? { ...bot, isStatic: true } : bot,
    )
  }

  return configuredBots
}

export function getConfiguredBot(botId: string): ConfiguredBot | undefined {
  return getConfiguredBots().find(bot => bot.id === botId)
}

export function getRuntimeContext(
  overrides: Partial<Pick<RuntimeContext, 'surface' | 'launchContext'>> = {},
): RuntimeContext {
  const normalizedSurface = overrides.surface ?? (isEmbeddedContext() ? 'helper_embed' : 'desktop_workspace')
  const launchContext = overrides.launchContext ?? 'cas'
  return {
    surface: normalizedSurface,
    launchContext,
    isEmbedded: normalizedSurface !== 'desktop_workspace',
  }
}

export function getSurfaceExperienceConfig({
  surface,
  bot,
}: {
  surface: ChatSurface
  bot: ConfiguredBot | undefined
}): SurfaceExperienceConfig {
  const config = getConfig()
  return {
    ...(config.surfaces?.[surface] ?? {}),
    ...(bot?.surfaces?.[surface] ?? {}),
  }
}

export function getDefaultConversationMode(
  surface: ChatSurface,
  authState: { isAuthenticated: boolean },
): ConversationPersistence {
  const config = getConfig()
  if (!authState.isAuthenticated) {
    return config.defaultAnonymousConversationMode ?? 'ephemeral'
  }
  return getSurfaceExperienceConfig({ surface, bot: undefined }).conversationMode
    ?? (surface === 'helper_embed' ? 'ephemeral' : 'persistent')
}
